import { useCallback, useEffect, useState } from 'react';
import { useImpersonation, getTokenClaims } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { registerOutcome } from './spherePermissionCache';
import type { PermissionOutcome, SpherePermissionAction } from './spherePermissionCache';

export type { SpherePermissionAction } from './spherePermissionCache';
export type { PermissionStatus } from './spherePermissionCache';
export { resetSpherePermissionCache } from './spherePermissionCache';

export interface UseSpherePermissionResult {
  allowed: boolean;
  loading: boolean;
  /** The check could not be answered (503, network). Not a denial; `allowed` stays false. */
  unavailable: boolean;
  /** Ask again after `unavailable`. */
  retry: () => void;
}

/**
 * Returns whether the authenticated user can perform `action` on `sphereId`.
 *
 * Cache is keyed by effective identity (`impersonationDetails.targetUserId ??
 * getTokenClaims().sub`) so results survive ApiClientsContext re-memoization
 * and impersonation switches are self-invalidating. Fails closed: an error
 * resolves to `{ allowed: false }` (the affordance stays hidden) and is not
 * cached, so the next mount of the gate asks again. `unavailable` tells such an
 * outage apart from a real denial.
 *
 * This is a UI gate only — the backend enforces permissions on every mutation.
 */
export function useSpherePermission(
  sphereId: string | undefined,
  action: SpherePermissionAction,
): UseSpherePermissionResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();

  const identityId = impersonationDetails?.targetUserId ?? getTokenClaims()?.sub;

  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const [answer, setAnswer] = useState<{ key: string; outcome: PermissionOutcome } | null>(null);

  const permissionClient = apiClients?.permissionClient;
  const key =
    sphereId && permissionClient && identityId
      ? `${identityId}|${sphereId}|${action}|${attempt}`
      : null;

  useEffect(() => {
    if (!key || !sphereId || !permissionClient || !identityId) return;

    let cancelled = false;
    void registerOutcome(sphereId, action, identityId, permissionClient).then((outcome) => {
      if (!cancelled) setAnswer({ key, outcome });
    });

    return () => {
      cancelled = true;
    };
  }, [key, sphereId, action, identityId, permissionClient]);

  // An answer counts only for the question it was asked for: until the current
  // key resolves, the gate is loading and closed, never the previous sphere's result.
  const outcome = key && answer?.key === key ? answer.outcome : null;
  const allowed = outcome === 'allowed';
  const unavailable = outcome === 'unavailable';
  const loading = key !== null && outcome === null;

  return { allowed, loading, unavailable, retry };
}
